import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { accountApi } from '../api/account';
import './CompleteProfilePage.css';

/**
 * CompleteProfilePage - Shown after login if firstName is missing.
 * Mirrors the mobile /complete-profile screen.
 * Saves firstName + lastName to account, then continues to intended destination.
 */
const CompleteProfilePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // The login page passes the intended destination via state
    const destination = location.state?.destination || '/';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!firstName.trim()) { toast.error('First name is required.'); return; }
        if (!lastName.trim()) { toast.error('Last name is required.'); return; }

        setIsSaving(true);
        try {
            const accountId = user?.accountId;
            if (!accountId) throw new Error('No account ID');

            await accountApi.updateAccount(accountId, {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
            });

            navigate(destination, { replace: true });
        } catch (err) {
            toast.error('Could not save your name. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="complete-profile-page">
            <div className="complete-profile-card">
                <h1 className="complete-profile-title">Tell us your name</h1>
                <p className="complete-profile-subtitle">
                    Your name helps schools and instructors identify you when you enrol in classes or book lessons.
                </p>

                <form onSubmit={handleSubmit} className="complete-profile-form">
                    <div className="complete-profile-field">
                        <label className="complete-profile-label" htmlFor="cp-first">
                            First name <span className="complete-profile-required">*</span>
                        </label>
                        <input
                            id="cp-first"
                            type="text"
                            className="complete-profile-input"
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            placeholder="Jane"
                            autoComplete="given-name"
                            autoFocus
                        />
                    </div>

                    <div className="complete-profile-field">
                        <label className="complete-profile-label" htmlFor="cp-last">
                            Last name <span className="complete-profile-required">*</span>
                        </label>
                        <input
                            id="cp-last"
                            type="text"
                            className="complete-profile-input"
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                            placeholder="Smith"
                            autoComplete="family-name"
                        />
                    </div>

                    <button
                        type="submit"
                        className="complete-profile-btn"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving…' : 'Continue'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CompleteProfilePage;
